import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { TimelineColumnElement } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-column.ts";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Point } from "@tento-core/point";
import { NaiveDate } from "@tento-chrono";
import { EventItemPresentation } from "@tento-lona";
import { EventItemAllDay, type Option } from "@tento-lona/sheets";

/** How close (px) to a cell edge a pointer must land to grab that edge. */
const EDGE_THRESHOLD_PX = 8;

@component({
  name: "lona-all-day-events-cell",
})
export class AllDayEventsCell extends LonaWebComponent {
  #eventData: Option<EventItemAllDay> = null;
  #ghostDateRange: Option<NaiveDate.Range> = null;

  /**
   * Show `e` as of `today`, in `scope`'s category colours and draft title
   * (`client.events`).
   */
  bind(
    e: EventItemAllDay,
    today: NaiveDate,
    scope: EventItemPresentation.Scope,
  ) {
    this.#eventData = e;
    this.#ghostDateRange = null;
    delete this.dataset.pending;
    // Rebound on every rendered-columns change by the fixed-child reconcile
    // (every scroll step), so every write here skips an equal value: a
    // same-value custom-property write still dirties style in Blink, and a
    // same-text `textContent` replaces the text node.
    const title = EventItemPresentation.title(e, scope) || "Untitled";
    const $content = this.$("content");
    if ($content.textContent !== title) $content.textContent = title;
    const isCurrent = e.range.end >= today.dse;
    const dimmedBg = isCurrent;
    this.assignStyles({
      "--cell-background-color": dimmedBg
        ? EventItemPresentation.backgroundColor(e, scope)
        : EventItemPresentation.dimmedBackgroundColor(e, scope),
      "--text-color": dimmedBg ? null : "var(--text-ter-on-solid)",
    });
    this.toggleAttribute("show-handles", false);
  }

  /**
   * Binds the cell to display a new event ghost (no existing event data).
   */
  bindNewEvent(
    title: string,
    backgroundColor: string,
    dateRange?: NaiveDate.Range,
  ): void {
    this.#eventData = null;
    this.#ghostDateRange = dateRange ?? null;
    this.dataset.pending = "true";
    this.$("content").textContent = title;
    this.style.setProperty("--cell-background-color", backgroundColor);
    this.style.setProperty("--text-color", "");
    this.toggleAttribute("show-handles", true);
  }

  #calculatePosition(
    offset: Point,
  ): Option<TimelineColumnElement.PointerPosition> {
    const width = this.offsetWidth || 1;
    const relativeOffset = Math.max(0, Math.min(1, offset.x / width));

    // Calculate which day within the span the pointer is over
    let startDate: NaiveDate;
    let endDate: NaiveDate;

    if (this.#eventData) {
      startDate = this.#eventData.time.start;
      endDate = this.#eventData.time.end;
    } else if (this.#ghostDateRange) {
      startDate = this.#ghostDateRange.start;
      endDate = this.#ghostDateRange.end;
    } else {
      return null;
    }

    const spanDays = endDate.dse - startDate.dse + 1;
    const cellWidthPx = width / spanDays;
    const dayIndex = Math.floor(relativeOffset * spanDays);
    const actualDate = startDate.addDays(Math.min(dayIndex, spanDays - 1));

    return {
      date: NaiveDate.Partial.fromDate(actualDate),
      relativeOffset,
      absoluteX: offset.x,
      cellWidthPx,
    };
  }

  #detectHandle(offset: Point): Option<"left" | "right"> {
    const width = this.offsetWidth || 1;
    if (offset.x <= EDGE_THRESHOLD_PX) return "left";
    if (offset.x >= width - EDGE_THRESHOLD_PX) return "right";
    return null;
  }

  #hasData(): boolean {
    return this.#eventData != null || this.#ghostDateRange != null;
  }

  #offsetFromEvent(e: PointerEvent): Point {
    const rect = this.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  setCallbacks(callbacks: TimelineColumnElement.CellCallbacks) {
    GESTURE_MANAGER.addPointerEvent(this, {
      onPointerDown: (offset) => {
        const position = this.#calculatePosition(offset);
        if (!position) return;
        callbacks.onPointerDown({
          position,
          handle: this.#detectHandle(offset),
        });
      },
      onClick: (_, offset) => {
        const position = this.#calculatePosition(offset);
        if (!position) return;
        callbacks.onPointerUp({ position });
      },
      onContextMenu: (_, offset) => {
        if (!this.#eventData) return;
        const position = this.#calculatePosition(offset);
        if (!position) return;
        callbacks.onContextMenu({ position, offset });
      },
    });

    this.onpointermove = (e) => {
      e.stopPropagation();
      if (!this.#hasData()) return;

      const offset = this.#offsetFromEvent(e);
      const position = this.#calculatePosition(offset);
      if (position) {
        // Dispatch moves while the cursor is over the bar; the full-width
        // strip surface (TimelineColumnElement, wired with asColumnDelegate) owns
        // pointer capture and continues the drag once the cursor leaves the
        // bar — mirroring how timeline[column]'s column surface continues a
        // timed-event drag. So the bar does NOT capture here.
        callbacks.onPointerMove({ position });
      }

      const handle = this.#detectHandle(offset);
      this.style.cursor = handle ? "ew-resize" : "pointer";
    };

    // Set up handle event listeners for ghost cells
    const $handleLeft = this.$("handle-left");
    const $handleRight = this.$("handle-right");

    const setupHandleEvents = (
      $handle: HTMLElement,
      handleType: "left" | "right",
    ) => {
      $handle.onpointerdown = (e) => {
        e.stopPropagation();
        const offset = this.#offsetFromEvent(e);
        const position = this.#calculatePosition(offset);
        if (!position) return;
        callbacks.onPointerDown({ position, handle: handleType });
      };
      $handle.onpointerup = (e) => {
        e.stopPropagation();
        const offset = this.#offsetFromEvent(e);
        const position = this.#calculatePosition(offset);
        if (position) {
          callbacks.onPointerUp({ position });
        }
      };
      $handle.onpointermove = (e) => {
        e.stopPropagation();
        const offset = this.#offsetFromEvent(e);
        const position = this.#calculatePosition(offset);
        if (position) {
          // Dispatch only; the strip surface owns capture and continues the
          // resize once the cursor leaves the (tiny) handle.
          callbacks.onPointerMove({ position });
        }
      };
    };

    setupHandleEvents($handleLeft, "left");
    setupHandleEvents($handleRight, "right");
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        border-radius: 4px;
        overflow: visible;
        margin-right: 2px;
        cursor: pointer;
        position: relative;
        transition: box-shadow 0.15s ease;
      }

      :host([active]),
      :host([data-pending="true"]) {
        box-shadow: rgba(0, 0, 0, 0.6) 0px 1px 3px 0.5px;
        z-index: 1;
      }

      #root {
        position: relative;
        padding: 4px 6px;
        height: 20px;
        justify-content: center;
        background: var(--cell-background-color);
        border-radius: inherit;
        overflow: hidden;
      }

      #content {
        color: var(--text-color, var(--text-sec-on-solid));
        white-space: nowrap;
      }

      .handle {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 6px;
        opacity: 0;
        pointer-events: none;
      }

      .handle-left {
        left: 0;
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
      }

      .handle-right {
        right: 0;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      :host([show-handles]) .handle {
        opacity: 1;
        pointer-events: auto;
        cursor: ew-resize;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <p id="content" class="small"></p>
      <div id="handle-left" class="handle handle-left"></div>
      <div id="handle-right" class="handle handle-right"></div>
    </std-col>
  `;
}
